// src/modules/myvault/components/canvasBuilder/CanvasRuntimeContext.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import type { CanvasDataSource, FilterOperator } from "../../types";
import type { CanvasWriteContext } from "../../services/viewService";

export interface CanvasRuntimeFilterCondition {
  field: string;
  operator: FilterOperator;
  value?: unknown;
}

export interface CanvasSelectedRow {
  elementId: string;
  canvasId: string | null;
  dataSourceAlias?: string;
  viewId?: string | null;
  rowId: string;
  rowData: Record<string, unknown>;
  selectedAt: number;
  selectionKeys: string[];
  provenancePath: string[];
}

export interface CanvasRuntimeFilter {
  id: string;
  sourceEventId: string;
  sourceElementId: string;
  sourceDataSourceAlias?: string;
  targetElementId?: string;
  targetCanvasId: string | null;
  targetDataSourceAlias: string;
  dependsOnSelectionKey: string;
  targetScopeKey: string;
  filters: CanvasRuntimeFilterCondition[];
  filterLogic?: "AND" | "OR";
  provenancePath: string[];
}

export type CanvasInteractionEvent =
  | {
      type: "row_selected";
      eventId?: string;
      elementId: string;
      canvasId: string | null;
      dataSourceAlias?: string;
      dataSourceAliases?: string[];
      viewId?: string | null;
      rowId: string;
      rowData: Record<string, unknown>;
      provenancePath?: string[];
    }
  | {
      type: "selection_cleared";
      eventId?: string;
      elementId?: string;
      canvasId: string | null;
      dataSourceAlias?: string;
      dataSourceAliases?: string[];
      provenancePath?: string[];
    };

export interface CanvasInteractionBus {
  selectedRows: Record<string, CanvasSelectedRow>;
  runtimeFilters: Record<string, CanvasRuntimeFilter[]>;
  dispatchInteraction: (event: CanvasInteractionEvent) => void;
  upsertRuntimeFilter: (filter: CanvasRuntimeFilter) => void;
  removeRuntimeFilter: (filterId: string) => void;
  getSelectedRow: (scope: {
    canvasId: string | null;
    elementId?: string;
    dataSourceAlias?: string;
  }) => CanvasSelectedRow | null;
  getRuntimeFilters: (scope: {
    canvasId: string | null;
    elementId?: string;
    dataSourceAlias?: string;
  }) => CanvasRuntimeFilter[];
}

export interface CanvasRuntime {
  onRefresh?: () => void | Promise<void>;
  workspaceId: string;
  canvasId: string | null;
  rowId: string | null;
  /** Primary record identity, independent of selection detail fetches. */
  primaryRowId?: string | null;
  rowData: Record<string, unknown> | null;
  embeddingPath?: CanvasWriteContext["embeddingPath"];
  dataSources?: CanvasDataSource[];
  canEdit?: boolean;
  selectionDetail?: {
    rowId: string | null;
    loading: boolean;
    error: string | null;
  };
  interactionBus: CanvasInteractionBus;
}

export interface CanvasRuntimeProviderValue {
  onRefresh?: () => void | Promise<void>;
  workspaceId: string;
  canvasId: string | null;
  rowId: string | null;
  /** Primary record identity, independent of selection detail fetches. */
  primaryRowId?: string | null;
  rowData: Record<string, unknown> | null;
  embeddingPath?: CanvasWriteContext["embeddingPath"];
  dataSources?: CanvasDataSource[];
  canEdit?: boolean;
  selectionDetail?: {
    rowId: string | null;
    loading: boolean;
    error: string | null;
  };
  onSelectionChange?: (event: CanvasInteractionEvent) => void;
  interactionBus?: CanvasInteractionBus;
}

interface InteractionState {
  selectedRows: Record<string, CanvasSelectedRow>;
  runtimeFilters: Record<string, CanvasRuntimeFilter[]>;
}

type InteractionAction =
  | { type: "DISPATCH"; event: CanvasInteractionEvent }
  | { type: "UPSERT_FILTER"; filter: CanvasRuntimeFilter }
  | { type: "REMOVE_FILTER"; filterId: string };

const CanvasRuntimeContext = createContext<CanvasRuntime | null>(null);

function canvasScope(canvasId: string | null): string {
  return `canvas:${canvasId || "root"}`;
}

export function buildElementSelectionKey(
  canvasId: string | null,
  elementId: string,
): string {
  return `${canvasScope(canvasId)}:element:${elementId}`;
}

export function buildDataSourceSelectionKey(
  canvasId: string | null,
  dataSourceAlias: string,
): string {
  return `${canvasScope(canvasId)}:dataSource:${dataSourceAlias}`;
}

export function buildRuntimeFilterScopeKey(
  canvasId: string | null,
  dataSourceAlias: string,
): string {
  return buildDataSourceSelectionKey(canvasId, dataSourceAlias);
}

function eventSelectionKeys(event: CanvasInteractionEvent): string[] {
  const keys: string[] = [];
  if (event.elementId) {
    keys.push(buildElementSelectionKey(event.canvasId, event.elementId));
  }
  if (event.dataSourceAlias) {
    keys.push(
      buildDataSourceSelectionKey(event.canvasId, event.dataSourceAlias),
    );
  }
  for (const alias of event.dataSourceAliases || []) {
    if (alias) keys.push(buildDataSourceSelectionKey(event.canvasId, alias));
  }
  return Array.from(new Set(keys));
}

function pathTouches(path: string[] | undefined, keys: Set<string>): boolean {
  return (path || []).some((key) => keys.has(key));
}

function collectCascadeKeys(
  state: InteractionState,
  seedKeys: string[],
): Set<string> {
  const keys = new Set(seedKeys);
  let changed = true;

  while (changed) {
    changed = false;

    for (const [selectionKey, row] of Object.entries(state.selectedRows)) {
      if (!keys.has(selectionKey) && pathTouches(row.provenancePath, keys)) {
        keys.add(selectionKey);
        changed = true;
      }
    }

    for (const filters of Object.values(state.runtimeFilters)) {
      for (const filter of filters) {
        if (
          keys.has(filter.dependsOnSelectionKey) ||
          pathTouches(filter.provenancePath, keys)
        ) {
          if (!keys.has(filter.targetScopeKey)) {
            keys.add(filter.targetScopeKey);
            changed = true;
          }
          if (filter.targetElementId) {
            const targetElementKey = buildElementSelectionKey(
              filter.targetCanvasId,
              filter.targetElementId,
            );
            if (!keys.has(targetElementKey)) {
              keys.add(targetElementKey);
              changed = true;
            }
          }
        }
      }
    }
  }

  return keys;
}

function removeCascadeState(
  state: InteractionState,
  seedKeys: string[],
): InteractionState {
  if (seedKeys.length === 0) return state;
  const cascadeKeys = collectCascadeKeys(state, seedKeys);
  const selectedRows = Object.fromEntries(
    Object.entries(state.selectedRows).filter(([key, row]) => {
      return (
        !cascadeKeys.has(key) && !pathTouches(row.provenancePath, cascadeKeys)
      );
    }),
  );
  const runtimeFilters = Object.fromEntries(
    Object.entries(state.runtimeFilters)
      .map(([key, filters]) => [
        key,
        filters.filter((filter) => {
          return (
            !cascadeKeys.has(filter.dependsOnSelectionKey) &&
            !cascadeKeys.has(filter.targetScopeKey) &&
            !pathTouches(filter.provenancePath, cascadeKeys)
          );
        }),
      ])
      .filter(([, filters]) => filters.length > 0),
  );
  return { selectedRows, runtimeFilters };
}

function interactionReducer(
  state: InteractionState,
  action: InteractionAction,
): InteractionState {
  if (action.type === "REMOVE_FILTER") {
    const runtimeFilters = Object.fromEntries(
      Object.entries(state.runtimeFilters)
        .map(([key, filters]) => [
          key,
          filters.filter((filter) => filter.id !== action.filterId),
        ])
        .filter(([, filters]) => filters.length > 0),
    );
    return { ...state, runtimeFilters };
  }

  if (action.type === "UPSERT_FILTER") {
    const filter = action.filter;
    const targetElementKey = filter.targetElementId
      ? buildElementSelectionKey(filter.targetCanvasId, filter.targetElementId)
      : null;
    if (
      filter.provenancePath.includes(filter.targetScopeKey) ||
      (targetElementKey && filter.provenancePath.includes(targetElementKey))
    ) {
      console.warn("Dropped cyclic canvas runtime filter", filter);
      return state;
    }

    const existing = state.runtimeFilters[filter.targetScopeKey] || [];
    return {
      ...state,
      runtimeFilters: {
        ...state.runtimeFilters,
        [filter.targetScopeKey]: [
          ...existing.filter((candidate) => candidate.id !== filter.id),
          filter,
        ],
      },
    };
  }

  const event = action.event;
  const seedKeys = eventSelectionKeys(event);
  const cleaned = removeCascadeState(state, seedKeys);

  if (event.type === "selection_cleared") {
    return cleaned;
  }

  const selectionKeys = eventSelectionKeys(event);
  const primaryKey = selectionKeys[0];
  if (!primaryKey) return cleaned;

  const selectedRow: CanvasSelectedRow = {
    elementId: event.elementId,
    canvasId: event.canvasId,
    dataSourceAlias: event.dataSourceAlias,
    viewId: event.viewId,
    rowId: event.rowId,
    rowData: event.rowData,
    selectedAt: Date.now(),
    selectionKeys,
    provenancePath: Array.from(
      new Set([...(event.provenancePath || []), ...selectionKeys]),
    ),
  };

  const selectedRows = { ...cleaned.selectedRows };
  for (const key of selectionKeys) {
    selectedRows[key] = selectedRow;
  }

  return { ...cleaned, selectedRows };
}

export function useCanvasRuntime(): CanvasRuntime | null {
  return useContext(CanvasRuntimeContext);
}

export function useCanvasInteractionBus(): CanvasInteractionBus | null {
  return useContext(CanvasRuntimeContext)?.interactionBus || null;
}

export const CanvasRuntimeProvider: React.FC<{
  value: CanvasRuntimeProviderValue;
  children: React.ReactNode;
}> = ({ value, children }) => {
  const [state, dispatch] = useReducer(interactionReducer, {
    selectedRows: {},
    runtimeFilters: {},
  });
  const upsertRuntimeFilter = useCallback(
    (filter: CanvasRuntimeFilter) =>
      dispatch({ type: "UPSERT_FILTER", filter }),
    [],
  );
  const removeRuntimeFilter = useCallback(
    (filterId: string) => dispatch({ type: "REMOVE_FILTER", filterId }),
    [],
  );

  const localBus = useMemo<CanvasInteractionBus>(() => {
    const getRuntimeFilters = (scope: {
      canvasId: string | null;
      elementId?: string;
      dataSourceAlias?: string;
    }) => {
      const keys = [
        scope.elementId
          ? buildElementSelectionKey(scope.canvasId, scope.elementId)
          : null,
        scope.dataSourceAlias
          ? buildRuntimeFilterScopeKey(scope.canvasId, scope.dataSourceAlias)
          : null,
      ].filter(Boolean) as string[];

      return keys.flatMap((key) => state.runtimeFilters[key] || []);
    };

    const getSelectedRow = (scope: {
      canvasId: string | null;
      elementId?: string;
      dataSourceAlias?: string;
    }) => {
      if (scope.elementId) {
        const byElement =
          state.selectedRows[
            buildElementSelectionKey(scope.canvasId, scope.elementId)
          ];
        if (byElement) return byElement;
      }
      if (scope.dataSourceAlias) {
        const byDataSource =
          state.selectedRows[
            buildDataSourceSelectionKey(scope.canvasId, scope.dataSourceAlias)
          ];
        if (byDataSource) return byDataSource;
      }

      if (!scope.elementId && scope.dataSourceAlias) {
        const sameCanvasSelections = new Map<string, CanvasSelectedRow>();
        for (const row of Object.values(state.selectedRows)) {
          if (row.canvasId !== scope.canvasId) continue;
          sameCanvasSelections.set(
            `${row.elementId}:${row.rowId}:${row.selectedAt}`,
            row,
          );
        }
        if (sameCanvasSelections.size === 1) {
          return Array.from(sameCanvasSelections.values())[0];
        }
      }
      return null;
    };

    return {
      selectedRows: state.selectedRows,
      runtimeFilters: state.runtimeFilters,
      dispatchInteraction: (event) => {
        dispatch({ type: "DISPATCH", event });
        value.onSelectionChange?.(event);
      },
      upsertRuntimeFilter,
      removeRuntimeFilter,
      getSelectedRow,
      getRuntimeFilters,
    };
  }, [
    state.runtimeFilters,
    state.selectedRows,
    value.onSelectionChange,
    upsertRuntimeFilter,
    removeRuntimeFilter,
  ]);

  const bus = value.interactionBus || localBus;
  const runtime = useMemo<CanvasRuntime>(
    () => ({
      onRefresh: value.onRefresh,
      workspaceId: value.workspaceId,
      canvasId: value.canvasId,
      rowId: value.rowId,
      primaryRowId: value.primaryRowId,
      rowData: value.rowData,
      embeddingPath: value.embeddingPath,
      dataSources: value.dataSources,
      canEdit: value.canEdit,
      selectionDetail: value.selectionDetail,
      interactionBus: bus,
    }),
    [
      value.onRefresh,
      bus,
      value.canvasId,
      value.rowData,
      value.embeddingPath,
      value.dataSources,
      value.canEdit,
      value.rowId,
      value.primaryRowId,
      value.selectionDetail,
      value.workspaceId,
    ],
  );

  return (
    <CanvasRuntimeContext.Provider value={runtime}>
      {children}
    </CanvasRuntimeContext.Provider>
  );
};
